import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Callout, GridItem, IconButton, Row, useIsInBottomSheet, useOverlayContext, useStateInRef } from 'seagull';
import { Edit } from 'seagull/icons/content-edit';
import { getTestIds } from 'seagull/utils/TestIds';
import ContactPicker from '~/components/ContactsPicker/ContactPicker';
import { ManageContactModalForVendor } from '~/components/ManageContactModal';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { NewContactModel } from '~/models/ContactModel';
import { selectors } from '~/store/state-selectors';
import { useStoreActions } from '~/store/store-context';
import { agentTargetForContact } from '../../../AgreementStudioModal/AgentChangeHighlight/agentTargetSets';
import { calculateCanEditContact, contactHasOtherActiveAgreements, handlePickerChange, noop } from './PrimaryContactPicker.utils';
export const GetPrimaryContactPickerTID = (parentTestId)=>getTestIds(parentTestId);
export const PrimaryContactPicker = (props)=>{
    const { t: contactPickerTexts } = useLocaleKeys('agreementStudio.proposalMode.contactPicker');
    const { existingContact, refreshExistingContacts, onChange, onValidation = noop, showCustomerDetailsWarning, hideEditContact, testId, disabled } = props;
    const { PrimaryContactPickerTestIds } = useMemo(()=>GetPrimaryContactPickerTID(testId), [
        testId
    ]);
    const getProps = useStateInRef(_object_spread_props(_object_spread({}, props), {
        openContactModal,
        newContactPostActions
    }));
    const { showModal, pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const isInBottomSheet = useIsInBottomSheet();
    const { api } = useStoreActions();
    const isViewMode = useStore(selectors.isViewMode);
    const [pickerResetKey, setPickerResetKey] = useState(0);
    const [canEditContact, setCanEditContact] = useState(()=>calculateCanEditContact(existingContact));
    useEffect(()=>{
        const newContact = NewContactModel.makeEmpty();
        if (newContact.hasPartialContactDetails() && existingContact == null) {
            handlePromise(api.contact.create(newContact)).then(async (created)=>{
                if (created.success) {
                    await getProps().newContactPostActions(created.data);
                } else {
                    const saved = await getProps().openContactModal({
                        newContactToCreate: created.data
                    });
                    await getProps().newContactPostActions(saved);
                }
            });
        }
        setCanEditContact(calculateCanEditContact(existingContact));
    }, [
        existingContact
    ]);
    async function openContactModal(modalProps) {
        const showAppliesEverywhere = contactHasOtherActiveAgreements(modalProps.existingContact);
        const sharedProps = _object_spread_props(_object_spread({}, modalProps), {
            testId: 'manage-contact-modal',
            origin: 'Agreement studio',
            appliesEverywhereCallout: showAppliesEverywhere ? /*#__PURE__*/ _jsx(Callout, {
                children: contactPickerTexts.appliesEverywhereCallout()
            }) : undefined
        });
        if (isInBottomSheet) {
            return new Promise((resolve)=>{
                setTimeout(()=>{
                    pushBottomSheetPage({
                        content: /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                            onClose: (result)=>{
                                popBottomSheetPage();
                                resolve(result);
                            }
                        }))
                    });
                }, 0);
            });
        }
        return showModal({
            biName: 'manage_contact_modal_for_vendor_modal',
            closeOnEscapeKey: false,
            closeOnClickOutside: false,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ManageContactModalForVendor, _object_spread_props(_object_spread({}, sharedProps), {
                    onClose: dismissModal
                }));
            }
        });
    }
    async function newContactPostActions(savedContactData) {
        if (savedContactData) {
            await refreshExistingContacts();
            onValidation(true);
            onChange({
                type: 'existingContact',
                id: savedContactData.id
            });
        } else {
            onValidation(false);
            setPickerResetKey((prev)=>prev + 1);
        }
    }
    const showEditButton = !hideEditContact && canEditContact;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GridItem, {
                span: showEditButton ? 11 : 12,
                "data-agent-target": existingContact != null ? agentTargetForContact(existingContact.id()) : undefined,
                children: /*#__PURE__*/ _jsx(ContactPicker, {
                    label: contactPickerTexts.primaryContactLabel(),
                    disabled: disabled,
                    hideSampleContact: isViewMode,
                    value: existingContact !== null && existingContact !== void 0 ? existingContact : NewContactModel.makeEmpty(),
                    onChange: async (change)=>handlePickerChange({
                            change,
                            onChange,
                            onValidation,
                            getProps
                        }),
                    testId: testId,
                    error: showCustomerDetailsWarning
                }, pickerResetKey)
            }),
            showEditButton && /*#__PURE__*/ _jsx(GridItem, {
                span: 1,
                children: /*#__PURE__*/ _jsx(Row, {
                    alignItems: "center",
                    children: /*#__PURE__*/ _jsx(IconButton, {
                        testId: PrimaryContactPickerTestIds.EditContactIcon(),
                        bi: {
                            name: 'edit_contact'
                        },
                        disabled: disabled,
                        tooltip: {
                            title: contactPickerTexts.editContactTooltip()
                        },
                        onClick: async ()=>{
                            const shouldRefresh = await openContactModal({
                                existingContact
                            });
                            if (shouldRefresh != null) {
                                await refreshExistingContacts();
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Edit, {})
                    })
                })
            })
        ]
    });
};
